import { forwardRef } from 'react';
import { alpha } from '@mui/material';
import { CustomContentProps } from 'notistack';
import { makeStyles } from 'tss-react/mui';

import { useTranslation } from 'modules/i18n';

import { translation } from './translation';

/**
 * Custom notification component.
 */
export const Notification = forwardRef<HTMLDivElement, CustomContentProps>(
  ({ message, action: _action, id }, ref) => {
    const action = typeof _action === 'function' ? _action(id) : _action;
    const { classes } = useStyles();
    const { t, keys } = useTranslation(translation);

    return (
      <div ref={ref} className={classes.root}>
        <div className={classes.action}>{action}</div>

        <div className={classes.header}>{t(keys.error)}</div>

        <div className={classes.content}>{message}</div>
      </div>
    );
  },
);

export const useStyles = makeStyles()(theme => ({
  root: {
    position: 'relative',
    padding: theme.spacing(4, 10, 4, 6),
    maxWidth: 400,
    width: '100%',
    background: theme.palette.primary.contrastText,
    borderRadius: 12,
    boxShadow: `0px 0px 40px 0px ${alpha(theme.palette.common.black, 0.3)}`,

    '&:before': {
      display: 'block',
      content: '""',
      position: 'absolute',
      inset: '0',
      borderRadius: 12,
      padding: theme.spacing(0.5),
      background: `linear-gradient(163.54deg, ${theme.palette.error.light} 3.74%, ${theme.palette.error.main} 13.27%, #5D1813 29.67%, ${theme.palette.background.default} 63.99%)`,
      mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
      maskComposite: 'exclude',
    },
  },
  action: {
    position: 'absolute',
    top: theme.spacing(2.5),
    right: theme.spacing(3),
    cursor: 'pointer',
  },
  header: {
    color: theme.palette.error.main,
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '22px',
  },
  content: {
    marginTop: theme.spacing(2),
    fontSize: 12,
    color: alpha(theme.palette.secondary.contrastText, 0.5),
  },
}));

Notification.displayName = 'Notification';
